export const OFFICE365_PHONES: Record<string, string> = {
  en: '1800 961 2888',
  es: '800 099 0352',
  de: '0800 723 8997',
  pt: '0800 020 1543',
};

export interface PhoneCountryRule {
  dialCode: string;
  /** Expected national number length (without country code). */
  nationalLength: number;
  /** Optional national-number pattern (digits only, no country code). */
  nationalPattern?: RegExp;
}

/** Country-specific phone rules keyed by ISO country code. */
export const PHONE_COUNTRY_RULES: Record<string, PhoneCountryRule> = {
  IN: {
    dialCode: '+91',
    nationalLength: 10,
  },
  US: {
    dialCode: '+1',
    nationalLength: 10,
    nationalPattern: /^[2-9]\d{9}$/,
  },
  CA: {
    dialCode: '+1',
    nationalLength: 10,
    nationalPattern: /^[2-9]\d{9}$/,
  },
};

export function dialCodeDigits(dialCode: string): string {
  return dialCode.replace(/\D/g, '');
}

export function phoneDigitsOnly(fullPhone: string): string {
  return fullPhone.replace(/\D/g, '');
}

export function phoneStartsWithDialCode(fullPhone: string, dialCode: string): boolean {
  const digits = phoneDigitsOnly(fullPhone);
  const prefix = dialCodeDigits(dialCode);
  return !!prefix && digits.startsWith(prefix);
}

export function extractNationalDigits(fullPhone: string, dialCode: string): string {
  const allDigits = phoneDigitsOnly(fullPhone);
  const prefix = dialCodeDigits(dialCode);
  if (!prefix || !allDigits.startsWith(prefix)) {
    return '';
  }
  return allDigits.slice(prefix.length);
}

export function getPhoneCountryRule(countryCode?: string): PhoneCountryRule | undefined {
  if (!countryCode) {
    return undefined;
  }
  return PHONE_COUNTRY_RULES[countryCode.toUpperCase()];
}

export function detectCountryCodeFromPhone(
  fullPhone: string,
  countries: Array<{ code: string; dial: string }>
): string | undefined {
  if (!fullPhone.startsWith('+')) {
    return undefined;
  }

  const match = [...countries]
    .sort((a, b) => b.dial.length - a.dial.length)
    .find((country) => fullPhone.startsWith(country.dial));

  return match?.code;
}

export function formatPhoneWithDialCode(dialCode: string, nationalDigits: string): string {
  const digits = nationalDigits.replace(/\D/g, '');
  if (!digits) {
    return dialCode;
  }
  return `${dialCode} ${digits}`;
}

export function clampNationalDigits(nationalDigits: string, maxLength: number): string {
  return nationalDigits.replace(/\D/g, '').slice(0, maxLength);
}

export interface PhoneValidationResult {
  valid: boolean;
  error?: 'phoneFormat';
}

export interface PhoneValidationOptions {
  countryCode?: string;
  dialCode?: string;
}

export function validatePhoneNumber(
  fullPhone: string,
  options: PhoneValidationOptions = {}
): PhoneValidationResult {
  const raw = (fullPhone ?? '').toString().trim();
  if (!raw) {
    return { valid: true };
  }

  if (!/^\+[\d\s().-]+$/.test(raw)) {
    return { valid: false, error: 'phoneFormat' };
  }

  const digits = phoneDigitsOnly(raw);
  if (!digits.length) {
    return { valid: false, error: 'phoneFormat' };
  }

  const countryCode = options.countryCode?.toUpperCase();
  const rule = countryCode ? getPhoneCountryRule(countryCode) : undefined;
  const expectedDial = options.dialCode ?? rule?.dialCode;

  if (expectedDial) {
    if (!phoneStartsWithDialCode(raw, expectedDial)) {
      return { valid: false, error: 'phoneFormat' };
    }

    const national = extractNationalDigits(raw, expectedDial);
    if (!national) {
      return { valid: false, error: 'phoneFormat' };
    }

    if (rule) {
      if (national.length !== rule.nationalLength) {
        return { valid: false, error: 'phoneFormat' };
      }
      if (rule.nationalPattern && !rule.nationalPattern.test(national)) {
        return { valid: false, error: 'phoneFormat' };
      }
      return { valid: true };
    }

    if (national.length < 4 || national.length > 14) {
      return { valid: false, error: 'phoneFormat' };
    }

    return { valid: true };
  }

  if (digits.length < 8 || digits.length > 15) {
    return { valid: false, error: 'phoneFormat' };
  }

  if (!/^[1-9]/.test(digits)) {
    return { valid: false, error: 'phoneFormat' };
  }

  return { valid: true };
}
